import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Modal, Select, Space, message } from 'antd';

import {
  listCategoriesAdmin,
  listDificuldades,
  listEdicoes,
  listTipos,
  updateJogo,
  updatePassatempo,
  type CatalogItem,
  type CategoryAdmin,
  type CognitiveDomain,
  type Dificuldade,
  type EdicaoAdmin,
  type JogoUpdatePayload,
  type PassatempoUpdatePayload,
  type TipoDePassatempo,
} from '../../../services/admin';
import { AcademyFields } from '../AcademyFields';
import { academyPayload, isTrainingEdicao } from '../academy';
import { apiErrorMessage } from '../desafios';
import { EdicaoCategoriesLine } from './EdicaoCategoriesLine';
import { EdicaoSelect } from './EdicaoSelect';
import { TemaSelect } from './TemaSelect';
import { catalogItemId } from './RowActions';

interface FormValues {
  titulo: string;
  subtitulo?: string;
  textoIntro?: string;
  edicoesId?: number;
  temaId?: number;
  tipoId?: number;
  dificuldadeId?: number;
  ordemJogo?: number;
  doDia?: string;
  cognitiveDomain?: CognitiveDomain;
  academyPoints?: number;
}

const trimmed = (v?: string): string | undefined => {
  const t = v?.trim();
  return t ? t : undefined;
};

interface Props {
  /** Item a editar; `null` mantém o modal fechado. */
  item: CatalogItem | null;
  onClose: () => void;
  onSaved: () => void;
}

export const GameInfoModal: React.FC<Props> = ({ item, onClose, onSaved }) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [edicoes, setEdicoes] = useState<EdicaoAdmin[]>([]);
  const [tipos, setTipos] = useState<TipoDePassatempo[]>([]);
  const [dificuldades, setDificuldades] = useState<Dificuldade[]>([]);
  const [categories, setCategories] = useState<CategoryAdmin[]>([]);
  const [pickedEdicao, setPickedEdicao] = useState<EdicaoAdmin | null>(null);
  const [edicaoLabel, setEdicaoLabel] = useState<string | null>(null);
  const [temaLabel, setTemaLabel] = useState<string | null>(null);
  const watchedEdicaoId = Form.useWatch('edicoesId', form);
  const isTraining = isTrainingEdicao(edicoes, watchedEdicaoId);
  // A edição recém-escolhida na busca pode não estar em `edicoes`; ela tem as categorias.
  const selectedEdicao =
    pickedEdicao && pickedEdicao.id === watchedEdicaoId
      ? pickedEdicao
      : edicoes.find((e) => e.id === watchedEdicaoId);

  useEffect(() => {
    if (!item) return;
    // Recarrega a cada abertura: o tipo da edição pode ter mudado na aba Edições.
    listEdicoes()
      .then(setEdicoes)
      .catch(() => message.error('Falha ao carregar as edições.'));
    listCategoriesAdmin()
      .then(setCategories)
      .catch(() => setCategories([]));
    listTipos()
      .then(setTipos)
      .catch(() => message.error('Falha ao carregar os tipos.'));
    listDificuldades()
      .then(setDificuldades)
      .catch(() => message.error('Falha ao carregar as dificuldades.'));
    setPickedEdicao(null);
    setEdicaoLabel(item.edicaoNome);
    setTemaLabel(item.temaNome);
    const base =
      item.source === 'passatempo'
        ? {
            titulo: item.passatempo.titulo ?? '',
            subtitulo: item.passatempo.subtitulo ?? undefined,
            edicoesId: item.passatempo.edicoesId ?? undefined,
            temaId: item.passatempo.temasPassatemposId ?? undefined,
            tipoId: item.passatempo.tiposDePassatemposId ?? undefined,
            dificuldadeId: item.passatempo.dificuldadesId ?? undefined,
            cognitiveDomain: item.passatempo.cognitiveDomain ?? undefined,
            academyPoints: item.passatempo.academyPoints ?? undefined,
          }
        : {
            titulo: item.jogo.titulo ?? '',
            subtitulo: item.jogo.subtitulo ?? undefined,
            edicoesId: item.jogo.edicoesId ?? undefined,
            temaId: item.jogo.idTema ?? undefined,
            tipoId: item.jogo.idTipo ?? undefined,
            dificuldadeId: item.jogo.idDificuldade ?? undefined,
            doDia: item.jogo.doDia ?? undefined,
            cognitiveDomain: item.jogo.cognitiveDomain ?? undefined,
            academyPoints: item.jogo.academyPoints ?? undefined,
          };
    form.setFieldsValue(base);
  }, [item, form]);

  const handleSubmit = async () => {
    if (!item) return;
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // erros de validação já exibidos pelo form
    }
    const subtitulo = trimmed(values.subtitulo);
    setSaving(true);
    try {
      if (item.source === 'passatempo') {
        const payload: PassatempoUpdatePayload = { titulo: values.titulo.trim() };
        if (subtitulo !== undefined) payload.subtitulo = subtitulo;
        if (values.edicoesId != null) payload.edicoesId = values.edicoesId;
        if (values.temaId != null) payload.temasPassatemposId = values.temaId;
        if (values.tipoId != null) payload.tiposDePassatemposId = values.tipoId;
        if (values.dificuldadeId != null) payload.dificuldadesId = values.dificuldadeId;
        Object.assign(payload, academyPayload(isTraining, values));
        await updatePassatempo(item.passatempo.id, payload);
      } else {
        const payload: JogoUpdatePayload = { titulo: values.titulo.trim() };
        if (subtitulo !== undefined) payload.subtitulo = subtitulo;
        const textoIntro = trimmed(values.textoIntro);
        if (textoIntro !== undefined) payload.textoIntro = textoIntro;
        if (values.edicoesId != null) payload.edicoesId = values.edicoesId;
        if (values.temaId != null) payload.idTema = values.temaId;
        if (values.tipoId != null) payload.idTipo = values.tipoId;
        if (values.dificuldadeId != null) payload.idDificuldade = values.dificuldadeId;
        if (values.ordemJogo != null) payload.ordemJogo = values.ordemJogo;
        const doDia = trimmed(values.doDia);
        if (doDia !== undefined) payload.doDia = doDia;
        Object.assign(payload, academyPayload(isTraining, values));
        await updateJogo(item.jogo.idJogo, payload);
      }
      message.success('Jogo atualizado.');
      onSaved();
      onClose();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? 'Falha ao salvar o jogo.');
    } finally {
      setSaving(false);
    }
  };

  const isJogo = item?.source === 'jogo';

  return (
    <Modal
      title={item ? `Informações do jogo #${catalogItemId(item)}` : 'Informações do jogo'}
      visible={item !== null}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Salvar"
      cancelText="Cancelar"
      confirmLoading={saving}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="titulo" label="Título" rules={[{ required: true, message: 'Informe o título do jogo.' }]}>
          <Input placeholder="Ex.: Caça-palavras dos animais" />
        </Form.Item>
        <Form.Item name="subtitulo" label="Subtítulo">
          <Input />
        </Form.Item>
        {isJogo && (
          <Form.Item name="textoIntro" label="Texto de introdução" extra="Deixe em branco para manter o valor atual.">
            <Input.TextArea rows={3} />
          </Form.Item>
        )}

        <Space size="large" wrap>
          <Form.Item
            name="edicoesId"
            label="Edição"
            extra={
              selectedEdicao ? (
                <EdicaoCategoriesLine categoryIds={selectedEdicao.categoryIds} categories={categories} />
              ) : undefined
            }
          >
            <EdicaoSelect
              style={{ width: 200 }}
              placeholder="Vincular a uma edição"
              valueLabel={edicaoLabel}
              onChange={(_id, edicao) => {
                setEdicaoLabel(edicao?.nome ?? null);
                setPickedEdicao(edicao ?? null);
                form.setFieldsValue({ temaId: undefined });
                setTemaLabel(null);
              }}
            />
          </Form.Item>
          <Form.Item name="temaId" label="Tema">
            <TemaSelect
              style={{ width: 200 }}
              placeholder="Vincular a um tema"
              edicoesId={watchedEdicaoId}
              valueLabel={temaLabel}
              onChange={(_id, tema) => setTemaLabel(tema?.nome ?? null)}
            />
          </Form.Item>
        </Space>

        <Space size="large" wrap>
          <Form.Item name="tipoId" label={isJogo ? 'Tipo' : 'Formato'}>
            <Select style={{ width: 200 }} placeholder="—">
              {tipos.map((t) => (
                <Select.Option key={t.id} value={t.id}>
                  {t.nomeFantasia ?? t.nome ?? `#${t.id}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="dificuldadeId" label="Dificuldade">
            <Select style={{ width: 160 }} placeholder="—">
              {dificuldades.map((d) => (
                <Select.Option key={d.id} value={d.id}>
                  {d.nome}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Space>

        {isJogo && (
          <Space size="large" wrap>
            <Form.Item name="ordemJogo" label="Ordem" extra="Deixe em branco para manter o valor atual.">
              <InputNumber style={{ width: 160 }} min={1} placeholder="Ex.: 1" />
            </Form.Item>
            <Form.Item name="doDia" label="Do dia (AAAA-MM-DD)">
              <Input style={{ width: 200 }} placeholder="Ex.: 2026-07-08" />
            </Form.Item>
          </Space>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <AcademyFields visible={isTraining} />
        </div>
      </Form>
    </Modal>
  );
};

export default GameInfoModal;
